// Plugin to draw SDG icons above bars

// Map SDG numbers to their public icon paths
// Icons are now served from public/sdg-icons/ to reduce bundle size
const sdgIcons = Object.fromEntries(Array.from({ length: 17 }, (_, i) => [i + 1, `/sdg-icons/image${i + 1}.png`]))

// Cache for loaded images
const imageCache = {}

// Load an image and cache it
const loadImage = (src, sdgNumber) => {
  return new Promise((resolve, reject) => {
    if (imageCache[sdgNumber]) {
      resolve(imageCache[sdgNumber])
      return
    }

    const img = new Image()
    img.onload = () => {
      imageCache[sdgNumber] = img
      resolve(img)
    }
    img.onerror = reject
    img.src = src
  })
}

// Extract SDG number from label (e.g., "SDG 1" -> 1)
const extractSdgNumber = label => {
  const match = label.match(/SDG\s*(\d+)/i)
  return match ? parseInt(match[1], 10) : null
}

export const sdgIconPlugin = {
  id: 'sdgIconPlugin',

  afterDatasetsDraw(chart) {
    const { ctx, scales } = chart
    const xScale = scales.x
    const yScale = scales.y

    if (!xScale || !yScale) return

    // Get the dataset
    const dataset = chart.data.datasets[0]
    if (!dataset) return

    // Get the x-axis baseline (where y = 0)
    const xAxisY = yScale.getPixelForValue(0)

    // Iterate through each data point
    chart.data.labels.forEach((label, index) => {
      const sdgNumber = extractSdgNumber(label)
      if (!sdgNumber) return

      const iconSrc = sdgIcons[sdgNumber]
      if (!iconSrc) return // Skip if icon doesn't exist

      // Check if image is already cached (loaded)
      if (!imageCache[sdgNumber]) {
        // Start loading the image and trigger chart update when done
        loadImage(iconSrc, sdgNumber)
          .then(() => {
            // Image is now cached, trigger chart redraw
            chart.update('none') // Update without animation
          })
          .catch(err => {
            console.warn(`Failed to load SDG ${sdgNumber} icon:`, err)
          })
        return // Don't draw this frame, wait for image to load
      }

      // Get bar element
      const meta = chart.getDatasetMeta(0)
      const barElement = meta.data[index]
      if (!barElement) return

      // Get bar properties
      const { x, y, width, options } = barElement
      const borderRadius = options.borderRadius || 4

      // Use cached image to draw synchronously
      const img = imageCache[sdgNumber]

      // Calculate image dimensions (width matches bar width, height maintains aspect ratio)
      const imgWidth = width
      const aspectRatio = img.height / img.width
      const imgHeight = imgWidth * aspectRatio

      // Position image at the top of the bar, centered horizontally
      const imgX = x - imgWidth / 2
      const imgY = y

      // Calculate the bottom of the image
      const imgBottom = imgY + imgHeight

      // Determine the clipping bottom (either image bottom or x-axis, whichever is higher)
      const clipBottom = Math.min(imgBottom, xAxisY)

      // Calculate visible height (may be less than imgHeight if clipped)
      const visibleHeight = clipBottom - imgY

      // Only draw if there's visible height
      if (visibleHeight <= 0) return

      // Save canvas state
      ctx.save()

      // Create clipping path with rounded corners at the top
      ctx.beginPath()
      ctx.moveTo(imgX + borderRadius, imgY)
      ctx.lineTo(imgX + imgWidth - borderRadius, imgY)
      ctx.arcTo(imgX + imgWidth, imgY, imgX + imgWidth, imgY + borderRadius, borderRadius)
      ctx.lineTo(imgX + imgWidth, clipBottom)
      ctx.lineTo(imgX, clipBottom)
      ctx.lineTo(imgX, imgY + borderRadius)
      ctx.arcTo(imgX, imgY, imgX + borderRadius, imgY, borderRadius)
      ctx.closePath()
      ctx.clip()

      // Draw the image inside the clipped area
      ctx.drawImage(img, imgX, imgY, imgWidth, imgHeight)

      // Restore canvas state
      ctx.restore()
    })
  }
}
